import fetchWrapper from '@/http/fetchWrapper';
import { api } from '@http/constants';
import {
  HOME_DOMAIN_SORT_FIELDS,
  type HomeDomainsRequest,
  type HomeDomainsResponse,
  type HomeDomainSortField,
} from '@eye-able/types';
import { useSuspenseQuery } from '@tanstack/react-query';

export type { HomeDomainData, HomeDomainsResponse } from '@eye-able/types';

/** Fields the API can order the overview by. */
export const REPORT_SORT_FIELDS = HOME_DOMAIN_SORT_FIELDS;

export type ReportSortField = HomeDomainSortField;

export function isReportSortField(field: string): field is ReportSortField {
  return (REPORT_SORT_FIELDS as readonly string[]).includes(field);
}

export type UseFetchHomeDomainsOptions = HomeDomainsRequest;

/**
 * The calculated report data of the domains the user can see. Search, pin
 * filtering, the target filter, sorting and paging all run on the API, over the
 * whole set rather than the page on screen.
 *
 * This suspends, so:
 * - no `enabled` — do not call it in report demo mode, render something else;
 * - no `placeholderData` — wrap option changes in `startTransition`, or they
 *   drop the caller to its nearest Suspense fallback;
 * - errors are thrown, and surface on the route's `errorElement`.
 */
export function useFetchHomeDomains({
  limit,
  offset,
  search,
  pinnedOnly,
  conformanceTargetLevels,
  sortBy,
  sortDirection,
}: UseFetchHomeDomainsOptions = {}) {
  const searchTerm = search?.trim();
  const targetLevels = conformanceTargetLevels?.length
    ? [...conformanceTargetLevels].sort().join(',')
    : undefined;

  return useSuspenseQuery({
    queryKey: [
      api.homeDomains,
      limit,
      offset,
      searchTerm,
      pinnedOnly,
      // Sorted above, so re-picking the same levels in another order is a hit.
      targetLevels,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      fetchWrapper.get<HomeDomainsResponse>(api.homeDomains, {
        // URLSearchParams would serialise an absent value as "undefined", which
        // the API rejects — omit instead. Same for a blank search term.
        ...(limit === undefined ? {} : { limit }),
        ...(offset === undefined ? {} : { offset }),
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(pinnedOnly === undefined ? {} : { pinnedOnly }),
        ...(targetLevels === undefined
          ? {}
          : { conformanceTargetLevels: targetLevels }),
        ...(sortBy === undefined ? {} : { sortBy }),
        ...(sortDirection === undefined ? {} : { sortDirection }),
      }),
  });
}
